import { generatePath } from 'react-router';
import { stripPathLocale } from '../../../../common/localeShared';
import type { TClassSummary, TSpecSummary } from '../../../../types/utilityTypes';
import { matchRoutePath } from '../../../libs/router/router-helpers';
import type { TUpsellQualificationRuntime } from '../types';

type TTalentBuildsUpsellQualificationBase = {
  classSlug: string;
  specSlug: string;
  season: string;
  region: string;
  destination: string;
};

export type TTalentBuildsUpsellQualification = TTalentBuildsUpsellQualificationBase &
  (
    | {
        placement: 'mythic_plus_spec_rankings';
        sourcePlacement:
          | 'mythic_plus_spec_rankings_top'
          | 'mythic_plus_spec_rankings_interstitial'
          | 'mythic_plus_spec_rankings_bottom';
      }
    | {
        placement: 'mythic_plus_class_rankings';
        sourcePlacement:
          | 'mythic_plus_class_rankings_top'
          | 'mythic_plus_class_rankings_interstitial'
          | 'mythic_plus_class_rankings_bottom';
      }
    | {
        placement: 'mythic_plus_role_rankings';
        sourcePlacement:
          | 'mythic_plus_role_rankings_top'
          | 'mythic_plus_role_rankings_interstitial'
          | 'mythic_plus_role_rankings_bottom';
      }
  );

export type TTalentBuildsUpsellPlacement = TTalentBuildsUpsellQualification['placement'];
export type TTalentBuildsUpsellSourcePlacement = TTalentBuildsUpsellQualification['sourcePlacement'];

const SUPPORTED_SEASONS = new Set(['season-mn-2']);
const SUPPORTED_REGIONS = new Set(['world', 'us', 'eu', 'kr', 'tw', 'cn']);
const TALENT_BUILDS_ROUTES = {
  characterRankings: '/mythic-plus-character-rankings/:season/:region/:class/:role/:page?',
  specRankings: '/mythic-plus-spec-rankings/:season/:region/:class/:spec/:page?',
  talents: '/specs/:specSlug/:type?'
} as const;

type TDestinationFilters = {
  classSlug?: string;
  specSlug?: string;
  season?: string;
  region?: string;
};

function resolveDestination(filters: TDestinationFilters): TTalentBuildsUpsellQualificationBase | null {
  const { classSlug, specSlug, season, region } = filters;
  if (
    !classSlug ||
    !specSlug ||
    classSlug === 'all' ||
    specSlug === 'all' ||
    !season ||
    !SUPPORTED_SEASONS.has(season) ||
    !region ||
    !SUPPORTED_REGIONS.has(region)
  ) {
    return null;
  }

  const path = generatePath(TALENT_BUILDS_ROUTES.talents, {
    specSlug: `${specSlug}-${classSlug}`,
    type: 'talents'
  });
  const searchParams = new URLSearchParams({
    activity: 'mythic-plus',
    season
  });

  if (region !== 'world') {
    searchParams.set('region', region);
  }

  return {
    classSlug,
    specSlug,
    season,
    region,
    destination: `${path}?${searchParams.toString()}`
  };
}

function getDeterministicIndex(value: string, length: number): number {
  let hash = 0;
  for (let index = 0; index < value.length; index++) {
    hash = (Math.imul(31, hash) + value.charCodeAt(index)) >>> 0;
  }
  return hash % length;
}

function qualifySpecRankings(
  runtime: TUpsellQualificationRuntime,
  pathname: string
): TTalentBuildsUpsellQualification | null {
  const match = matchRoutePath({ path: TALENT_BUILDS_ROUTES.specRankings, end: true }, pathname);
  if (!match) {
    return null;
  }

  const destination = resolveDestination({
    classSlug: match.params.class,
    specSlug: match.params.spec,
    season: match.params.season,
    region: match.params.region
  });
  if (!destination) {
    return null;
  }

  const placement = 'mythic_plus_spec_rankings';
  return {
    placement,
    sourcePlacement: `mythic_plus_spec_rankings_${runtime.source}` as const,
    ...destination
  };
}

function getCharacterRankingsCandidates(
  classes: readonly TClassSummary[],
  specs: readonly TSpecSummary[],
  classSlug?: string,
  role?: string
) {
  const selectedClass = classSlug && classSlug !== 'all' ? classes.find((cls) => cls.slug === classSlug) : undefined;
  if (classSlug !== 'all' && !selectedClass) {
    return [];
  }

  const hasRoleFilter = !!role && role !== 'all';
  const classesById = new Map(classes.map((cls) => [cls.id, cls]));

  return specs
    .filter((spec) => (!selectedClass || spec.class_id === selectedClass.id) && (!hasRoleFilter || spec.role === role))
    .map((spec) => ({ spec, cls: classesById.get(spec.class_id) }))
    .filter((candidate): candidate is { spec: TSpecSummary; cls: TClassSummary } => !!candidate.cls)
    .sort((left, right) => {
      const classComparison = left.cls.slug.localeCompare(right.cls.slug);
      return classComparison || left.spec.slug.localeCompare(right.spec.slug);
    });
}

function qualifyCharacterRankings(
  runtime: TUpsellQualificationRuntime,
  pathname: string
): TTalentBuildsUpsellQualification | null {
  const match = matchRoutePath({ path: TALENT_BUILDS_ROUTES.characterRankings, end: true }, pathname);
  if (!match) {
    return null;
  }

  const classSlug = match.params.class;
  const role = match.params.role;
  const hasClassFilter = !!classSlug && classSlug !== 'all';
  const hasRoleFilter = !!role && role !== 'all';
  if (!hasClassFilter && !hasRoleFilter) {
    return null;
  }

  const candidates = getCharacterRankingsCandidates(runtime.siteMeta.classes, runtime.siteMeta.specs, classSlug, role);
  if (candidates.length === 0) {
    return null;
  }

  const selectionKey = [
    `seed:${runtime.siteConfig.seed}`,
    hasClassFilter ? `class:${classSlug}` : '',
    hasRoleFilter ? `role:${role}` : ''
  ]
    .filter(Boolean)
    .join('|');
  const selectedCandidate = candidates[getDeterministicIndex(selectionKey, candidates.length)];
  const destination = resolveDestination({
    classSlug: selectedCandidate.cls.slug,
    specSlug: selectedCandidate.spec.slug,
    season: match.params.season,
    region: match.params.region
  });
  if (!destination) {
    return null;
  }

  if (hasClassFilter) {
    const placement = 'mythic_plus_class_rankings';
    return {
      placement,
      sourcePlacement: `mythic_plus_class_rankings_${runtime.source}` as const,
      ...destination
    };
  }

  const placement = 'mythic_plus_role_rankings';
  return {
    placement,
    sourcePlacement: `mythic_plus_role_rankings_${runtime.source}` as const,
    ...destination
  };
}

export function qualifyTalentBuildsCrossPromo(
  runtime: TUpsellQualificationRuntime
): TTalentBuildsUpsellQualification | null {
  if (runtime.game !== 'wowretail' || runtime.siteConfig.isEmbedded) {
    return null;
  }

  const pathname = stripPathLocale(runtime.pathname);
  return qualifySpecRankings(runtime, pathname) || qualifyCharacterRankings(runtime, pathname);
}
